import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { MDButton } from '../../../components/MDButton';
import { MDTypography } from '../../../components/MDTypography';
import { PaymentRequest } from '../types/finance.types';

interface PaymentExpandableContentProps {
  paymentRequest: PaymentRequest;
  onBillAction: (
    billId: string,
    action: string,
    paymentRequestId: string
  ) => void;
  loading: boolean;
}

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const PaymentExpandableContent: React.FC<PaymentExpandableContentProps> = ({
  paymentRequest,
  onBillAction,
  loading,
}) => {
  const { t } = useTranslation();

  if (!paymentRequest.bills || paymentRequest.bills.length === 0) {
    return (
      <Box p={3}>
        <MDTypography variant="body2" color="secondary">
          {t('No bills found for this payment request')}
        </MDTypography>
      </Box>
    );
  }

  const bills = paymentRequest.bills.filter((billRef) => billRef.bill);
  return (
    <Box p={2}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('Bill Id')}</TableCell>
            <TableCell>{t('Bill Type')}</TableCell>
            {/* <TableCell>{t('Date of Service')}</TableCell> */}
            <TableCell align="right">{t('Total Billed')}</TableCell>
            <TableCell align="right">{t('Total Saving')}</TableCell>
            <TableCell align="right">{t('Total Allowed')}</TableCell>
            <TableCell align="right">{t('Total Fee')}</TableCell>
            <TableCell align="right">{t('Total Due')}</TableCell>
            <TableCell>{t('Actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {bills.map((billRef) => {
            const bill = billRef;
            return (
              <TableRow key={bill.billId}>
                <TableCell>{bill.billId}</TableCell>
                <TableCell>{bill.bill?.billType}</TableCell>
                <TableCell align="right">
                  {formatter.format(bill.bill?.billedCharges || 0)}
                </TableCell>
                <TableCell align="right">
                  {formatter.format(bill.bill?.savingsAmount || 0)}
                </TableCell>
                <TableCell align="right">
                  {formatter.format(bill.bill?.payable || 0)}
                </TableCell>
                <TableCell align="right">
                  {formatter.format(bill.bill?.costContainmentFees || 0)}
                </TableCell>
                <TableCell align="right">
                  {formatter.format(bill.bill?.totalDue || 0)}
                </TableCell>
                <TableCell>
                  <Box display="flex" style={{ gap: '8px' }}>
                    <MDButton
                      size="small"
                      color="info"
                      variant="outlined"
                      onClick={() =>
                        onBillAction(
                          bill.billId,
                          'view-details',
                          paymentRequest.id
                        )
                      }
                      disabled={loading}
                    >
                      {t('Details')}
                    </MDButton>

                    {billRef.bill?.verdict?.verdict != 'paid' && (
                      <MDButton
                        size="small"
                        color="warning"
                        variant="outlined"
                        onClick={() =>
                          onBillAction(
                            bill.billId,
                            'move-to-next-payment',
                            paymentRequest.id
                          )
                        }
                        disabled={loading}
                      >
                        {t('Move to next payment')}
                      </MDButton>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

export default PaymentExpandableContent;
